import { useAtom, useSetAtom } from 'jotai';
import { useCallback } from 'react';
import * as state from '@/components/Analysis/state';
import { FilterPill } from './InsightsSearchBar.types';

export function useInsightsFilterState() {
  const [selectedTemplateFilters, setSelectedTemplateFilters] = useAtom(
    state.selectedTemplateFiltersState
  );
  const [selectedDeckIds, setSelectedDeckIds] = useAtom(state.selectedFilteredDeckIds);
  const [selectedPaintRegionIds, setSelectedPaintRegionIds] = useAtom(
    state.selectedFilteredPaintRegionIds
  );
  const [selectedWorkpackIds, setSelectedWorkpackIds] = useAtom(state.selectedFilteredWorkpackIds);
  const [activeFieldId, setActiveFieldId] = useAtom(state.activeTemplateFieldId);
  const [searchTerm, setSearchTermState] = useAtom(state.insightsSearchBarSearchTerm);
  const [rangeSearchTerm, setRangeSearchTermState] = useAtom(
    state.insightsSearchBarRangeSearchTerm
  );
  const setApplyFilters = useSetAtom(state.applyFilters);
  const setRefetchEquipmentTable = useSetAtom(state.refetchEquipmentTable);
  const setRefetchUpdatedPois = useSetAtom(state.refetchUpdatedPois);

  const removePill = useCallback(
    (pill: FilterPill) => {
      switch (pill.type) {
        case 'template': {
          setSelectedTemplateFilters((prev) => {
            const index = prev.findIndex((p) => p.id === pill.fieldKey);
            if (index === -1) return prev;

            const existing = prev[index];
            const filteredValues = existing.value.filter((v) => v !== pill.value);

            if (filteredValues.length === 0) {
              return [...prev.slice(0, index), ...prev.slice(index + 1)];
            }
            return [
              ...prev.slice(0, index),
              { ...existing, value: filteredValues },
              ...prev.slice(index + 1),
            ];
          });
          break;
        }
        case 'deck': {
          setSelectedDeckIds((prev) => prev.filter((id) => id !== pill.value));
          break;
        }
        case 'paintRegion': {
          setSelectedPaintRegionIds((prev) => prev.filter((id) => id !== pill.value));
          break;
        }
        case 'workpack': {
          setSelectedWorkpackIds((prev) => prev.filter((id) => id !== pill.value));
          break;
        }
        case 'searchTerm': {
          setSearchTermState((prev) => {
            const terms = prev ? prev.split('||') : [];
            return terms.filter((term) => term !== pill.value).join('||');
          });
          break;
        }
        case 'rangeSearchTerm': {
          setRangeSearchTermState('');
          break;
        }
      }
      setApplyFilters(true);
      setRefetchUpdatedPois(true);
    },
    [
      setSelectedTemplateFilters,
      setSelectedDeckIds,
      setSelectedPaintRegionIds,
      setSelectedWorkpackIds,
      setSearchTermState,
      setRangeSearchTermState,
      setApplyFilters,
      setRefetchUpdatedPois,
    ]
  );

  const clearAllFilters = useCallback(() => {
    setSelectedTemplateFilters([]);
    setSelectedDeckIds([]);
    setSelectedPaintRegionIds([]);
    setSelectedWorkpackIds([]);
    setSearchTermState('');
    setRangeSearchTermState('');
    setApplyFilters(true);
    setRefetchEquipmentTable((count) => (count ?? 0) + 1);
    setRefetchUpdatedPois(true);
  }, [
    setSelectedTemplateFilters,
    setSelectedDeckIds,
    setSelectedPaintRegionIds,
    setSelectedWorkpackIds,
    setSearchTermState,
    setRangeSearchTermState,
    setApplyFilters,
    setRefetchEquipmentTable,
    setRefetchUpdatedPois,
  ]);

  const addTemplateOption = useCallback(
    (fieldKey: string, optionValue: string) => {
      setSelectedTemplateFilters((prev) => {
        const existingIndex = prev.findIndex((p) => p.id === fieldKey);
        if (existingIndex === -1) {
          return [...prev, { id: fieldKey, value: [optionValue] }];
        }

        const existing = prev[existingIndex];
        if (existing.value.includes(optionValue)) return prev;

        return [
          ...prev.slice(0, existingIndex),
          { ...existing, value: [...existing.value, optionValue] },
          ...prev.slice(existingIndex + 1),
        ];
      });
    },
    [setSelectedTemplateFilters]
  );

  const addDeckFilter = useCallback(
    (id: string) => {
      setSelectedDeckIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    },
    [setSelectedDeckIds]
  );

  const addPaintRegionFilter = useCallback(
    (id: string) => {
      setSelectedPaintRegionIds([id]);
    },
    [setSelectedPaintRegionIds]
  );

  const addWorkpackFilter = useCallback(
    (id: string) => {
      setSelectedWorkpackIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    },
    [setSelectedWorkpackIds]
  );

  const setSearchTerm = useCallback(
    (term: string | ((prev: string) => string)): void => {
      if (typeof term === 'function') {
        setSearchTermState(term);
        return;
      }

      setSearchTermState((prev) => {
        const existingTerms = prev ? prev.split('||').filter((t) => t.trim()) : [];
        const trimmedTerm = term.trim();
        if (existingTerms.includes(trimmedTerm)) return prev;
        return [...existingTerms, trimmedTerm].join('||');
      });
    },
    [setSearchTermState]
  );

  const setRangeSearchTerm = useCallback(
    (term: string) => {
      setRangeSearchTermState(term);
    },
    [setRangeSearchTermState]
  );

  return {
    selectedTemplateFilters,
    selectedDeckIds,
    selectedPaintRegionIds,
    selectedWorkpackIds,
    activeFieldId,
    setActiveFieldId,
    removePill,
    clearAllFilters,
    addTemplateOption,
    addDeckFilter,
    addPaintRegionFilter,
    addWorkpackFilter,
    setApplyFilters,
    searchTerm,
    setSearchTerm,
    rangeSearchTerm,
    setRangeSearchTerm,
  };
}
